class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.originalButton = null;
    this.stickyButton = null;
    this.productForm = null;
    this.observer = null;
    this.sectionId = this.dataset.sectionId;
    this.productFormId = this.dataset.productFormId;
  }

  connectedCallback() {
    if (!this.sectionId || !this.productFormId) return;

    this.originalButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.stickyButton = this.querySelector('.sticky-add-to-cart__button');
    this.productForm = document.getElementById(this.productFormId);

    if (!this.originalButton || !this.stickyButton || !this.productForm) {
      this.style.display = 'none';
      return;
    }

    this.initObserver();
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));
    this.initVariantListener();
    this.initPriceListener();
    this.initFooterObserver();
    this.initCartListener();
    this.initRdStationButtonObserver();
    this.initAdoptControllerButtonObserver();
    this.initWhatsAppButtonObserver();
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      this.hideSticky();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
    window.removeEventListener('scroll', this.handleScroll);
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  initObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = entry.boundingClientRect;
        const isAboveViewport = rect.bottom < 0;
        
        if (!entry.isIntersecting && isAboveViewport && !this.isNearFooter()) {
          this.classList.add('visible');
          document.body.classList.add('sticky-add-to-cart-visible');
          this.adjustRdStationButton();
          this.adjustAdoptControllerButton();
          this.adjustWhatsAppButton();
        } else {
          this.classList.remove('visible');
          document.body.classList.remove('sticky-add-to-cart-visible');
          this.adjustRdStationButton();
          this.adjustAdoptControllerButton();
          this.adjustWhatsAppButton();
        }
      });
    }, options);

    this.observer.observe(this.originalButton);
  }

  initFooterObserver() {
    this.handleScroll = () => {
      const isNearFooter = this.isNearFooter();
      const originalButtonRect = this.originalButton.getBoundingClientRect();
      const isAboveViewport = originalButtonRect.bottom < 0;
      const isVisible = originalButtonRect.top >= 0 && originalButtonRect.top < window.innerHeight;
      
      if (isNearFooter) {
        this.classList.remove('visible');
        document.body.classList.remove('sticky-add-to-cart-visible');
        this.adjustRdStationButton();
        this.adjustAdoptControllerButton();
        this.adjustWhatsAppButton();
      } else if (isAboveViewport && !isVisible) {
        this.classList.add('visible');
        document.body.classList.add('sticky-add-to-cart-visible');
        this.adjustRdStationButton();
        this.adjustAdoptControllerButton();
        this.adjustWhatsAppButton();
      } else {
        this.classList.remove('visible');
        document.body.classList.remove('sticky-add-to-cart-visible');
        this.adjustRdStationButton();
        this.adjustAdoptControllerButton();
        this.adjustWhatsAppButton();
      }
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    this.handleResize = () => {
      if (document.body.classList.contains('sticky-add-to-cart-visible')) {
        this.adjustRdStationButton();
        this.adjustAdoptControllerButton();
        this.adjustWhatsAppButton();
      }
    };
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  isNearFooter() {
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const clientHeight = window.innerHeight;
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
    const stickyHeight = this.offsetHeight || 100;
    
    return distanceToBottom <= stickyHeight;
  }

  handleStickyButtonClick(e) {
    e.preventDefault();
    
    if (this.stickyButton.disabled || this.stickyButton.classList.contains('loading')) {
      return;
    }

    const productFormElement = this.productForm.closest('product-form');
    if (!productFormElement || !productFormElement.form) {
      return;
    }

    const originalSubmitButton = productFormElement.querySelector('[type="submit"]');
    if (!originalSubmitButton || originalSubmitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.stickyButton.classList.add('loading');
    const spinner = this.stickyButton.querySelector('.loading__spinner');
    const buttonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');
    
    if (spinner) spinner.classList.remove('hidden');
    if (buttonText) buttonText.style.opacity = '0';

    originalSubmitButton.click();

    const handleCartUpdate = () => {
      this.stickyButton.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.style.opacity = '1';
      this.hideSticky();
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-disabled') {
          if (!originalSubmitButton.classList.contains('loading')) {
            handleCartUpdate();
            observer.disconnect();
          }
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (!originalSubmitButton.classList.contains('loading')) {
            handleCartUpdate();
            observer.disconnect();
          }
        }
      });
    });

    observer.observe(originalSubmitButton, {
      attributes: true,
      attributeFilter: ['aria-disabled', 'class']
    });

    setTimeout(() => {
      if (this.stickyButton.classList.contains('loading')) {
        handleCartUpdate();
        observer.disconnect();
      }
    }, 5000);
  }

  initVariantListener() {
    const variantInput = this.productForm.querySelector('.product-variant-id');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        this.updateStickyButtonState();
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled']
      });

      variantInput.addEventListener('change', () => {
        this.updateStickyButtonState();
      });
    }
  }

  initPriceListener() {
    const priceElement = document.getElementById(`price-${this.sectionId}`);
    if (priceElement) {
      const observer = new MutationObserver(() => {
        this.updateStickyPrice();
      });

      observer.observe(priceElement, {
        childList: true,
        subtree: true
      });
    }
  }

  updateStickyButtonState() {
    const variantInput = this.productForm.querySelector('.product-variant-id');
    if (!variantInput) return;

    const isDisabled = variantInput.disabled || variantInput.value === '';
    this.stickyButton.disabled = isDisabled;

    const originalButtonText = this.originalButton.querySelector('span');
    const stickyButtonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');
    
    if (originalButtonText && stickyButtonText) {
      stickyButtonText.textContent = originalButtonText.textContent.trim();
    }
  }

  updateStickyPrice() {
    const priceElement = document.getElementById(`price-${this.sectionId}`);
    const stickyPriceElement = document.getElementById(`StickyPrice-${this.sectionId}`);
    
    if (!priceElement || !stickyPriceElement) {
      return;
    }

    const priceContent = priceElement.querySelector('.price');
    if (!priceContent) {
      return;
    }

    const clonedPrice = priceContent.cloneNode(true);
    stickyPriceElement.innerHTML = '';
    stickyPriceElement.appendChild(clonedPrice);
    
    const priceDiv = stickyPriceElement.querySelector('.price');
    if (priceDiv) {
      priceDiv.classList.add('price--sticky');
    }
  }

  initCartListener() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (cartDrawer.classList.contains('active')) {
              this.hideSticky();
            }
          }
        });
      });

      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    const checkCartDrawer = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) {
        this.hideSticky();
      }
    };

    document.addEventListener('cart:updated', () => {
      this.hideSticky();
      setTimeout(checkCartDrawer, 100);
    });

    document.addEventListener('cart:update', () => {
      this.hideSticky();
      setTimeout(checkCartDrawer, 100);
    });
  }

  adjustFloatingButton(buttonId, fixedBottom = null) {
    const button = document.querySelector(buttonId);
    if (button) {
      if (!button.dataset.originalBottom) {
        const computedStyle = window.getComputedStyle(button);
        button.dataset.originalBottom = computedStyle.bottom || '40px';
      }

      const isWhatsAppButton = button.classList.contains('float');
      const displayValue = isWhatsAppButton ? 'flex' : 'block';

      if (document.body.classList.contains('sticky-add-to-cart-visible')) {
        let bottomValue;
        if (fixedBottom !== null) {
          bottomValue = `${fixedBottom}px`;
        } else {
          const stickyHeight = this.offsetHeight || 100;
          const spacing = 20;
          bottomValue = `${stickyHeight + spacing}px`;
        }
        button.style.setProperty('bottom', bottomValue, 'important');
        button.style.setProperty('display', displayValue, 'important');
        button.style.setProperty('visibility', 'visible', 'important');
        button.style.setProperty('opacity', '1', 'important');
      } else {
        const originalBottom = button.dataset.originalBottom;
        if (originalBottom) {
          button.style.setProperty('bottom', originalBottom, 'important');
        } else {
          button.style.removeProperty('bottom');
        }
        button.style.setProperty('display', displayValue, 'important');
        button.style.setProperty('visibility', 'visible', 'important');
        button.style.setProperty('opacity', '1', 'important');
      }
    }
  }

  adjustRdStationButton() {
    const isDesktop = window.innerWidth >= 750;
    const bottomValue = isDesktop ? 67 : 137;
    this.adjustFloatingButton('.rdstation-popup-position-bottom_right', bottomValue);
  }

  adjustAdoptControllerButton() {
    this.adjustFloatingButton('#adopt-controller-button');
  }

  adjustWhatsAppButton() {
    const stickyHeight = this.offsetHeight || (window.innerWidth >= 750 ? 75 : 85);
    const spacing = 10;
    const bottomValue = stickyHeight + spacing;
    this.adjustFloatingButton('.float', bottomValue);
  }

  initFloatingButtonObserver(buttonSelector, adjustFunction) {
    const checkButton = setInterval(() => {
      const button = document.querySelector(buttonSelector);
      if (button) {
        adjustFunction.call(this);
        clearInterval(checkButton);
      }
    }, 500);

    setTimeout(() => clearInterval(checkButton), 10000);

    adjustFunction.call(this);
  }

  initRdStationButtonObserver() {
    this.initFloatingButtonObserver('.rdstation-popup-position-bottom_right', this.adjustRdStationButton);
  }

  initAdoptControllerButtonObserver() {
    this.initFloatingButtonObserver('#adopt-controller-button', this.adjustAdoptControllerButton);
  }

  initWhatsAppButtonObserver() {
    this.initFloatingButtonObserver('.float', this.adjustWhatsAppButton);
  }

  hideSticky() {
    this.classList.remove('visible');
    document.body.classList.remove('sticky-add-to-cart-visible');
    this.adjustRdStationButton();
    this.adjustAdoptControllerButton();
    this.adjustWhatsAppButton();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

